import React, { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import PriorityHighIcon from '@mui/icons-material/PriorityHigh';
import { Box, Divider, Typography, useTheme } from '@ublend-npm/aulaui-core';
import NavigationBackHeader from '@app/components/presentational/common/NavigationBackHeader/NavigationBackHeader';
import {
  fetchConnectedEmailDetail,
  clearConnectedEmailDetail,
} from '@app/actions/integrations/emails';
import { getOutlookEmailDetail } from '@core/selectors/integrations';
import { getHomeViewRole } from '@app/selectors/home';
import { emailDetailToOutlookThread } from './useOutlookEmails';
import { OutlookThreadMessage } from './types';
import OutlookThreadMessageCard from './components/OutlookThreadMessageCard';
import OutlookEmailDetailSkeleton from './components/OutlookEmailDetailSkeleton';
import { trackOutlookReplyClicked } from './outlookAnalytics';

const PROVIDER = 'microsoft';

interface OutlookThreadMobileScreenProps {
  routeEmailId?: string;
}

const OutlookThreadMobileScreen: React.FC<OutlookThreadMobileScreenProps> = ({
  routeEmailId,
}) => {
  const theme = useTheme();
  const dispatch = useDispatch<any>();
  const homeViewRole = useSelector(getHomeViewRole);
  const detailState = useSelector(getOutlookEmailDetail);

  const selectedThread = useMemo(
    () =>
      detailState.rawDetail
        ? emailDetailToOutlookThread(detailState.rawDetail)
        : null,
    [detailState.rawDetail]
  );
  const isLoadingDetail = detailState.isLoadingDetail;

  const [expandedIds, setExpandedIds] = useState<string[]>([]);
  const messageRefs = React.useRef<Record<string, HTMLDivElement | null>>({});

  const messages = selectedThread?.messages ?? [];
  const orderedMessages = [...messages].reverse();
  const hasHighImportance = orderedMessages.some((m) => m.isImportant);

  const activeTargetId = routeEmailId || selectedThread?.targetMessageId;

  useEffect(() => {
    if (routeEmailId) {
      dispatch(fetchConnectedEmailDetail(PROVIDER, routeEmailId));
    }
    return () => {
      dispatch(clearConnectedEmailDetail());
    };
  }, [routeEmailId, dispatch]);

  useEffect(() => {
    if (selectedThread && orderedMessages.length > 0) {
      const target =
        activeTargetId && orderedMessages.some((m) => m.id === activeTargetId)
          ? activeTargetId
          : orderedMessages[0].id;
      setExpandedIds([target]);
    }
  }, [selectedThread?.id, activeTargetId, orderedMessages.length]);

  // Scroll to target message card when thread is loaded
  useEffect(() => {
    if (isLoadingDetail || !selectedThread || orderedMessages.length === 0)
      return;

    const target =
      activeTargetId && orderedMessages.some((m) => m.id === activeTargetId)
        ? activeTargetId
        : orderedMessages[0]?.id;

    if (!target) return;

    const timer = setTimeout(() => {
      const targetElement = messageRefs.current[target];
      if (targetElement) {
        targetElement.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    }, 150);

    return () => clearTimeout(timer);
  }, [
    selectedThread?.id,
    activeTargetId,
    isLoadingDetail,
    orderedMessages.length,
  ]);

  const toggleMessage = (id: string) => {
    setExpandedIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );
  };

  const handleReply = (message: OutlookThreadMessage) => {
    const webLink = message.webLink;
    if (webLink) {
      trackOutlookReplyClicked(homeViewRole);
      window.open(webLink, '_blank', 'noopener,noreferrer');
    }
  };

  const handleBack = () => {
    window.history.back();
  };

  return (
    <Box
      sx={{
        display: 'flex',
        width: '100%',
        height: '100%',
        overflowY: 'hidden',
        p: 2,
        pt: 1,
        gap: 1.5,
        flexDirection: 'column',
      }}
    >
      <NavigationBackHeader onClick={handleBack} />

      {isLoadingDetail || !selectedThread ? (
        <OutlookEmailDetailSkeleton />
      ) : (
        <Box
          display="flex"
          flexDirection="column"
          flex={1}
          minHeight={0}
          gap={2}
          p={2}
          borderRadius={2}
          boxShadow={
            theme.palette.mode === 'light'
              ? '0 8px 20px rgba(0,0,0,0.05)'
              : '0 4px 12px rgba(0,0,0,0.15)'
          }
          sx={{
            bgcolor: theme.palette.background.paper,
          }}
        >
          {/* Thread subject + importance badge */}
          <Box
            display="flex"
            alignItems="center"
            justifyContent="space-between"
            gap={2}
            sx={{ flexWrap: 'wrap' }}
          >
            <Typography
              variant="h6"
              fontWeight="medium"
              sx={{
                color: (theme) =>
                  theme.palette.mode === 'dark' ? '#FFFFFF' : 'text.primary',
              }}
            >
              {selectedThread?.subject || '(No Subject)'}
            </Typography>

            {hasHighImportance ? (
              <Box display="flex" alignItems="center" gap={0.5} flexShrink={0}>
                <PriorityHighIcon sx={{ color: '#D32F2F', fontSize: 18 }} />
                <Typography
                  variant="subtitle2"
                  fontWeight={600}
                  sx={{ color: '#D32F2F' }}
                >
                  High Importance
                </Typography>
              </Box>
            ) : null}
          </Box>

          <Divider />

          {/* Scrollable message cards */}
          <Box
            sx={{
              flex: 1,
              minHeight: 0,
              overflowY: 'auto',
              overflowX: 'hidden',
              scrollbarWidth: 'none',
              msOverflowStyle: 'none',
              '&::-webkit-scrollbar': { display: 'none' },
            }}
          >
            {orderedMessages.map((message) => (
              <Box
                key={message.id}
                ref={(el: HTMLDivElement | null) => {
                  messageRefs.current[message.id] = el;
                }}
              >
                <OutlookThreadMessageCard
                  message={message}
                  isExpanded={expandedIds.includes(message.id)}
                  onToggle={toggleMessage}
                  onReply={handleReply}
                />
                <Divider sx={{ marginTop: 1, marginBottom: 2 }} />
              </Box>
            ))}
          </Box>
        </Box>
      )}
    </Box>
  );
};

export default OutlookThreadMobileScreen;
